/*
 * product-option-accordion.js — summary text for
 * snippets/product-option-accordion.liquid's swatch-grid / price-
 * list rows.
 *
 * DRY contract (same shape for every option, no per-option-name
 * branching):
 *   [data-option-accordion="<name>"]      the <details> row
 *   [data-option-summary="<name>"]        summary value text node
 *   [data-option-summary-price="<name>"]  summary price text node
 *   [data-option-value-label] / [data-option-value-price]
 *     on every swatch/list-row label inside the row's body.
 *   [data-option-note-when] on an optional caption under the body —
 *     shown only when the committed selection contains that needle
 *     (Stone → "white lab diamond").
 *
 * One render function (`renderSummary`) is the only place that
 * writes to the summary — called on hover (preview, uncommitted)
 * and on the radio's native `change` event (commit). Desktop
 * hover previews and reverts on mouseleave; touch devices have no
 * hover so only the change-triggered commit path ever fires,
 * matching the "click commits on mobile" requirement without a
 * separate code path.
 *
 * The variant-resolution engine (product-purchase-form.js) is
 * untouched — this module only reads the DOM, it doesn't
 * participate in variant lookup.
 *
 * Scroll fade — list bodies (`[data-option-list]` for native Size /
 * Carat, `[data-ring-size-list]` for metafield ring sizes) get
 * `data-scroll-bottom` toggled to show/hide the bottom fade overlay
 * (::after on `.product-option-accordion__body--list`) declared in
 * component-product-option-accordion.css, based on scroll position.
 */

const ROW_SELECTOR = '[data-option-accordion]';
const INSTANCE_KEY = '_optionAccordionBound';

function swatchStyleFrom(target) {
  const bg = target?.querySelector?.('.product-option-accordion__swatch-bg');
  return bg?.getAttribute('style') || '';
}

function paintSummarySwatch(row, style) {
  const optionName = row.dataset.optionAccordion;
  const summarySwatch = row.querySelector(`[data-option-summary-swatch="${cssEscape(optionName)}"]`);
  if (!summarySwatch) return;

  if (style) {
    summarySwatch.setAttribute('style', style);
  } else {
    summarySwatch.removeAttribute('style');
  }

  const box = summarySwatch.closest('.product-option-accordion__summary-swatch-box');
  box?.classList.toggle(
    'product-option-accordion__summary-swatch-box--image',
    !!style && style.includes('background-image')
  );
}

function renderSummary(row, label, price, style, { updateSwatch = false } = {}) {
  const optionName = row.dataset.optionAccordion;
  const summary = row.querySelector(`[data-option-summary="${cssEscape(optionName)}"]`);
  const summaryPrice = row.querySelector(`[data-option-summary-price="${cssEscape(optionName)}"]`);
  if (summary) summary.textContent = label;
  if (summaryPrice) summaryPrice.textContent = price || '';
  // ponytail: copy inline style string; rebuild DOM if images need <img>
  if (updateSwatch && style != null) paintSummarySwatch(row, style);
}

function markRowSelected(row) {
  row.dataset.summarySwatchPopulated = 'true';
  row.classList.add('product-option-accordion--selected');
}

function summarySwatchPopulated(row) {
  return row.dataset.summarySwatchPopulated === 'true';
}

/**
 * Lock the flush 44px summary slot only when the row closes after a
 * real selection — staging while open reflows the header (label jump).
 * List rows (Size / Carat) never take --slot.
 */
function stageSummarySlot(row) {
  if (!row.classList.contains('product-option-accordion--has-summary-swatch')) return;
  if (row.classList.contains('product-option-accordion--slot')) return;
  row.classList.add('product-option-accordion--slot');
}

function cssEscape(value) {
  return window.CSS && CSS.escape ? CSS.escape(value) : value;
}

function committedState(row) {
  const checked = row.querySelector('.product-option-accordion__input:checked');
  const target = checked?.nextElementSibling;
  return {
    label: target?.dataset.optionValueLabel ?? '',
    price: target?.dataset.optionValuePrice ?? '',
    style: swatchStyleFrom(target),
  };
}

/**
 * Stone-type caption (`data-option-note-when`) — show only when the
 * committed selection contains the needle (e.g. "white lab diamond").
 */
function syncOptionNote(row) {
  const note = row.querySelector('[data-option-note-when]');
  if (!note) return;

  const when = (note.getAttribute('data-option-note-when') || '').trim().toLowerCase();
  if (!when) return;

  const label = (committedState(row).label || '').trim().toLowerCase();
  if (label.includes(when)) note.removeAttribute('hidden');
  else note.setAttribute('hidden', '');
}

function bindRow(row) {
  if (row[INSTANCE_KEY]) return;
  row[INSTANCE_KEY] = true;

  const canHover = window.matchMedia('(hover: hover)').matches;

  // Shopify already has a selected / first-available value — mirror that
  // into the summary swatch on load. Closed rows also stage --slot so the
  // flush swatch shows without waiting for a shopper click.
  syncInitialSelection(row);
  syncOptionNote(row);

  row.addEventListener('toggle', () => {
    if (!row.open && summarySwatchPopulated(row)) stageSummarySlot(row);
  });

  row.querySelectorAll('[data-option-value-label]').forEach((target) => {
    // List rows (Size / Carat) must not preview on hover — the summary
    // always reflects the committed selection, only the radio's `change`
    // updates it (client feedback: hovering another size should not
    // change the displayed size name/price before the user clicks it).
    const isListRow = target.classList.contains('product-option-accordion__row');

    if (canHover && !isListRow) {
      target.addEventListener('mouseenter', () => {
        // Hover previews the value's absolute $ CONTRIBUTION (written by
        // product-price-contributions.js) — the same figure the summary
        // will commit to if the value is selected. Never a ±$ delta:
        // contributions are waterfall terms, so a raw price delta would
        // not reconcile with the committed numbers (client feedback).
        renderSummary(row, target.dataset.optionValueLabel, target.dataset.optionValuePrice, swatchStyleFrom(target), {
          updateSwatch: summarySwatchPopulated(row),
        });
      });
      target.addEventListener('mouseleave', () => {
        const { label, price, style } = committedState(row);
        renderSummary(row, label, price, style, { updateSwatch: summarySwatchPopulated(row) });
      });
    }

    const input = target.previousElementSibling;
    if (input && input.type === 'radio') {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        markRowSelected(row);
        renderSummary(row, target.dataset.optionValueLabel, target.dataset.optionValuePrice, swatchStyleFrom(target), {
          updateSwatch: true,
        });
        syncOptionNote(row);
      });
    }
  });
}

/**
 * Hydrate from the checked radio (selected_or_first_available_variant).
 * Liquid already SSR-paints closed swatch rows; this covers section
 * reloads and any row that still needs JS paint.
 */
function syncInitialSelection(row) {
  const checked = row.querySelector('.product-option-accordion__input:checked');
  if (!checked) return;

  markRowSelected(row);

  if (!row.classList.contains('product-option-accordion--has-summary-swatch')) return;

  const style = swatchStyleFrom(checked.nextElementSibling);
  if (style) paintSummarySwatch(row, style);
  if (!row.open) stageSummarySlot(row);
}

const LIST_SELECTOR = '[data-option-list], [data-ring-size-list]';
const LIST_INSTANCE_KEY = '_optionListScrollBound';
// 1px slack so sub-pixel scroll rounding doesn't leave a fade stuck on.
const SCROLL_EDGE_SLACK = 1;

function syncListScrollFades(list) {
  const canScroll = list.scrollHeight > list.clientHeight + SCROLL_EDGE_SLACK;
  const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - SCROLL_EDGE_SLACK;

  list.dataset.scrollBottom = String(canScroll && !atBottom);
}

function bindListScroll(list) {
  if (list[LIST_INSTANCE_KEY]) return;
  list[LIST_INSTANCE_KEY] = true;

  syncListScrollFades(list);
  list.addEventListener('scroll', () => syncListScrollFades(list), { passive: true });

  if (window.ResizeObserver) {
    new ResizeObserver(() => syncListScrollFades(list)).observe(list);
  }

  // Metafield ring-size rows are rebuilt via innerHTML (variant change /
  // first paint). Content height can change without the list's box size
  // changing, so ResizeObserver alone misses it.
  if (window.MutationObserver) {
    new MutationObserver(() => syncListScrollFades(list)).observe(list, {
      childList: true,
    });
  }

  // Closed <details> report clientHeight 0; remeasure when the accordion opens.
  const details = list.closest('details');
  details?.addEventListener('toggle', () => {
    if (details.open) syncListScrollFades(list);
  });
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(ROW_SELECTOR).forEach(bindRow);
  scope.querySelectorAll(LIST_SELECTOR).forEach(bindListScroll);
}

function syncAllOptionNotes(root) {
  const scope = root || document;
  scope.querySelectorAll(ROW_SELECTOR).forEach(syncOptionNote);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));

// Metal switch / matrix rematch may flip radios without a `change` event.
document.addEventListener('variant:update', (e) => {
  const section =
    e.target && e.target.closest ? e.target.closest('.shopify-section') : null;
  syncAllOptionNotes(section || document);
});
